<template>
  <div>
    <b-row>
      <b-col :lg="6">
        <h1 class="page-title section-title title-slash">
          {{ $t('transactions.index.title') }}
        </h1>
      </b-col>
    </b-row>
    <b-row class="mb-3">
      <b-col :cols="12">
        <b-dropdown
          id="filters-dropdown"
          :text="currentTransactionFilter"
          class="w-100 d-block d-md-none"
          block
          menu-class="w-100"
        >
          <b-dropdown-item
            v-for="filter in transactionFilters"
            :key="filter.value"
            @click.native="currentTransactionFilter = filter.value"
          >
            {{ filter.text }}
          </b-dropdown-item>
        </b-dropdown>
        <b-form-radio-group
          class="transactions-invoices-filters d-none d-md-block"
          id="btn-radios"
          buttons
          button-variant="outline-primary"
          v-model="currentTransactionFilter"
          :options="transactionFilters"
          name="radioBtnOutline"
        />
      </b-col>
    </b-row>
    <b-row>
      <b-col :cols="12" :md="12" :xl="9">
        <div class="transactions-table">
          <b-card class="transactions-card">
            <b-table
              ref="table"
              class="table-clean"
              :per-page="10"
              :current-page="currentPage"
              :fields="fields[currentTransactionFilter]"
              :items="loadTransactions"
            >
              <template #table-busy>
                <loader :height="500" />
              </template>
              <template v-slot:cell(action)="data">
                <download-dropdown
                  :options="fetchDownloadLinks(data)"
                  @fireEvent="dropdownEvent"
                />
              </template>
              <template v-slot:cell(value)="data">
                <div v-if="data.value">
                  {{ data.value | currency(data.item.currsym, null, 0) }}
                </div>
                <span
                  class="d-inline-block"
                  v-if="shouldConversionVisible && data.item.transaction_amount"
                >
                  {{ $t('transactions.index.transactionAmount.recieved') }}
                  <span class="font-weight-bold mx-1">
                    {{ `₹${commaSepratedValue(data.item.transaction_amount)}` }}
                  </span>
                  {{ $t('transactions.index.transactionAmount.at') }} ₹{{
                    data.item.rate
                  }}
                  {{ $t('transactions.index.transactionAmount.per') }}
                  {{ data.item.base_currency }}
                </span>
              </template>
              <template v-slot:cell(disbursed)="data">
                <div v-if="data.item.disbursed">
                  {{ data.item.disbursed | moment('MMM D, YYYY') }}
                </div>
              </template>
              <template v-slot:cell(dated)="data">
                <div v-if="data.item.dated">
                  {{ data.item.dated | moment('MMMM D, YYYY') }}
                </div>
              </template>
              <template v-slot:cell(settlement_date)="data">
                <div v-if="data.item.settlement_date">
                  {{ data.item.settlement_date | moment('MMM D, YYYY') }}
                </div>
              </template>
              <template v-slot:cell(invoice_reference)="data">
                <router-link
                  :to="{
                    name: 'invoicesSingle',
                    params: { id: data.item.invoice_id }
                  }"
                  v-track-click
                  :id="`invoices-ref-${data.item.invoice_id}`"
                >
                  {{ data.item.invoice_reference }}
                </router-link>
              </template>
              <template v-slot:cell(reference)="data">
                <router-link
                  :to="{
                    name: 'invoicesSingle',
                    params: { id: data.item.id }
                  }"
                  v-track-click
                  :id="`invoices-ref-${data.item.invoice_id}`"
                >
                  {{ data.item.reference }}
                </router-link>
              </template>
              <template v-slot:cell(customer_bank)="data">
                <div>{{ data.value }}</div>
                <drip-chip
                  chipText="Received in INR"
                  v-if="shouldConversionVisible && data.item.transaction_amount"
                />
              </template>
              <template v-slot:cell(settlement_customer_bank)="data">
                <div>{{ data.value }}</div>
                <drip-chip
                  chipText="Received in INR"
                  v-if="shouldConversionVisible && data.item.transaction_amount"
                />
              </template>
            </b-table>
          </b-card>
          <b-pagination
            :total-rows="totalPages"
            align="fill"
            v-model="currentPage"
            class="mt-3 bg-white"
            :per-page="10"
          />
        </div>
      </b-col>
    </b-row>
  </div>
</template>
<script>
import Loader from '@/components/common/Loader'
import DripChip from '@/components/common/Chip'
import DownloadDropdown from '@/components/common/DownloadDropdown'
import { freshdeskMixin } from '@/mixins/Freshdesk'
import transactionService from '@/services/transactions'
import invoiceService from '@/services/invoice'
import downloadService from '@/services/download/'
import { commaSeparator } from '@/utils/common'
import map from 'lodash/map'

export default {
  data() {
    return {
      currentPage: 1,
      totalPages: 1,
      transactions: {
        advances: [],
        payments: [],
        settlements: []
      },
      currentTransactionFilter: 'advances',
      loaded: {
        advances: false,
        payments: false,
        settlements: false
      }
    }
  },
  computed: {
    transactionFilters() {
      return this.$t('transactions.index.transactionFilters')
    },
    fields() {
      return this.$t('transactions.index.fields')
    },
    shouldConversionVisible() {
      return ['advances', 'settlements'].includes(this.currentTransactionFilter)
    }
  },
  mounted() {
    document.title = this.$t('transactions.index.htmlTitle')
    this.$mpTrack('Transactions page mounted')
    if (this.$store.state.account.country === 'India') {
      this.showAndPrefillFreshdesk({ cf_category_i: 'Invoice related' })
    }
  },
  methods: {
    fetchDownloadLinks(data) {
      return [
        {
          text: this.$t('common.invoiceSwift'),
          data: {
            id:
              data.item.type === 'advances'
                ? data.item?.advances?.[0]?.id // advance id
                : data.item.id,
            invType: this.currentTransactionFilter,
            type: 'swift',
            ref: data.item.reference || data.item.invoice_reference,
            date:
              data.item.disbursed ||
              data.item.settlement_date ||
              data.item.dated
          },
          disabled: !data.item.swiftAvail
        },
        {
          text: this.$t('common.invoiceAdvice'),
          data: {
            id: data.item.invoice_id,
            type: 'advice'
          },
          disabled: false
        },
        {
          text: this.$t('common.invoiceNoc'),
          data: {
            id:
              data.item.type === 'advances'
                ? data.item?.advances?.[0]?.id // advance id
                : data.item.id,
            type: 'noc',
            ref: data.item.reference || data.item.invoice_reference,
            date:
              data.item.disbursed ||
              data.item.settlement_date ||
              data.item.dated
          },
          disabled: !data.item.nocAvail
        }
      ]
    },
    downloadInvoiceAdvice(invId) {
      this.$store.dispatch('setDownloadNotification', {
        fileDownloadNotification: true
      })
      return invoiceService
        .downloadInvoice(invId)
        .then((res) => {
          downloadService.downloadPDF(res.data, `invoices-${invId}-advice.pdf`)
          this.$store.dispatch('setDownloadNotification', {
            fileDownloadNotification: false
          })
        })
        .catch((e) => {
          console.log(e)
          this.$store.dispatch('setDownloadNotification', {
            fileDownloadNotification: false
          })
        })
    },
    async downloadInvoiceSwiftCopy(invId, invRef, invDate) {
      if (invId) {
        this.$store.dispatch('setDownloadNotification', {
          fileDownloadNotification: true
        })
        if (this.currentTransactionFilter === 'advances') {
          let res = await invoiceService
            .downloadSwiftCopy('advances', invId)
            .catch(console.log)
          if (res.status === 204) {
            this.$notify({
              title: 'File not found!',
              type: 'error'
            })
          }
          const isPDF = res.data.type === 'application/pdf'
          let filename = downloadService.createFilename(
            isPDF,
            'advance',
            invRef,
            invDate
          )
          downloadService.downloadFile(res.data, filename)
        } else {
          let res = await invoiceService
            .downloadSwiftCopy('invoices', invId, 'settlement')
            .catch(console.log)
          if (res.status === 204) {
            this.$notify({
              title: 'File not found!',
              type: 'error'
            })
          }
          const isPDF = res.data.type === 'application/pdf'
          let filename = downloadService.createFilename(
            isPDF,
            'settlement',
            invRef,
            invDate
          )
          downloadService.downloadFile(res.data, filename)
        }
        this.$store.dispatch('setDownloadNotification', {
          fileDownloadNotification: false
        })
      } else {
        this.$notify({
          title: 'No document found!',
          type: 'error'
        })
      }
    },
    async downloadNocDocument(invId, invRef, invDate) {
      if (invId) {
        this.$store.dispatch('setDownloadNotification', {
          fileDownloadNotification: true
        })
        if (this.currentTransactionFilter === 'advances') {
          let res = await invoiceService
            .downloadNocCopy('advances', invId)
            .catch(console.log)
          if (res.status === 204) {
            this.$notify({
              title: 'File not found!',
              type: 'error'
            })
          }
          const isPDF = res.data.type === 'application/pdf'
          let filename = downloadService.createFilename(
            isPDF,
            'advance',
            invRef,
            invDate
          )
          downloadService.downloadFile(res.data, filename)
        } else {
          let res = await invoiceService
            .downloadNocCopy('invoices', invId, 'settlement')
            .catch(console.log)
          if (res.status === 204) {
            this.$notify({
              title: 'File not found!',
              type: 'error'
            })
          }
          const isPDF = res.data.type === 'application/pdf'
          let filename = downloadService.createFilename(
            isPDF,
            'settlement',
            invRef,
            invDate
          )
          downloadService.downloadFile(res.data, filename)
        }
        this.$store.dispatch('setDownloadNotification', {
          fileDownloadNotification: false
        })
      } else {
        this.$notify({
          title: 'No document found!',
          type: 'error'
        })
      }
    },
    dropdownEvent(links) {
      if (links.data && links.data.type === 'advice') {
        this.$mpTrack('Advance invoices - Download Invoice advice')
        this.downloadInvoiceAdvice(links.data.id)
      } else if (links.data && links.data.type === 'noc') {
        this.$mpTrack('Advance invoices - Download Noc Document')
        this.downloadNocDocument(links.data.id, links.data.ref, links.data.date)
      } else {
        this.$mpTrack('Advance invoices - Download Swift copy')
        this.downloadInvoiceSwiftCopy(
          links.data.id,
          links.data.ref,
          links.data.date
        )
      }
    },
    serviceToCall(activeTab) {
      this.$mpTrack(`Transaction tab - ${activeTab} clicked`)
      if (activeTab === 'payments') {
        return {
          service: transactionService.loadTransactionsPayments,
          type: 'payments'
        }
      } else if (activeTab === 'advances') {
        return {
          service: transactionService.loadTransactionsAdvances,
          type: 'advance'
        }
      } else if (activeTab === 'settlements') {
        return {
          service: transactionService.loadTransactionsSettlements,
          type: 'settlement'
        }
      }
    },
    changeTab(type) {
      this.currentTransactionFilter = type
      this.currentPage = 1
      this.$refs.table.refresh()
    },
    commaSepratedValue(value) {
      return commaSeparator(value)
    },
    async loadTransactions(ctx) {
      try {
        const { service, type } = this.serviceToCall(
          this.currentTransactionFilter
        )
        const res = await service(
          this.$store.state.account.accountId,
          ctx && ctx.currentPage
        )

        const RESPONSE_MAP = {
          advance: 'drip/business/advances',
          settlement: 'drip/business/invoices',
          payments: 'drip/business/payments'
        }

        this.totalPages = res.data && res.data.meta.pagination.total_objects
        const idsToFetch = map(res.data[RESPONSE_MAP[type]], 'id')
        const dataToSend = {
          // type: type === 'advances' ? 'advance' : type,
          type: type,
          attribute_ids: idsToFetch
        }

        const { data: conversionDataWithId } =
          await transactionService.fetchTransactionsConversionRates(
            this.$store.getters.user.id,
            dataToSend
          )
        const mergedWithConvData = res.data[RESPONSE_MAP[type]].map(
          (transactions) => ({
            ...transactions,
            ...conversionDataWithId.find(
              (convData) => convData.transaction_id === transactions.id
            )
          })
        )

        if (this.currentTransactionFilter === 'advances') {
          const resData = await invoiceService.checkSwiftCopy(
            'advances',
            mergedWithConvData.map((x) => x.id)
          )
          const resData2 = await invoiceService.checkNocCopy(
            'advances',
            mergedWithConvData.map((x) => x.id)
          )

          if (resData.data) {
            Object.keys(resData.data).forEach((v) => {
              const index = mergedWithConvData.findIndex(
                (x) => x.id === parseInt(v)
              )
              mergedWithConvData[index].swiftAvail = resData.data[v]
            })
          }
          if (resData2.data) {
            Object.keys(resData2.data).forEach((v) => {
              const index = mergedWithConvData.findIndex(
                (x) => x.id === parseInt(v)
              )
              mergedWithConvData[index].nocAvail = resData2.data[v]
            })
          }
        }
        if (this.currentTransactionFilter === 'settlements') {
          const resData2 = await invoiceService.checkSwiftCopy(
            'invoices',
            mergedWithConvData.map((x) => x.invoice_id || x.id)
          )
          const resData3 = await invoiceService.checkNocCopy(
            'invoices',
            mergedWithConvData.map((x) => x.id)
          )
          if (resData2.data) {
            Object.keys(resData2.data).forEach((v) => {
              const index = mergedWithConvData.findIndex(
                (x) => x.id === parseInt(v)
              )
              mergedWithConvData[index].swiftAvail =
                (resData2.data[v] && resData2.data[v].settlement) || false
            })
          }
          if (resData3.data) {
            Object.keys(resData3.data).forEach((v) => {
              const index = mergedWithConvData.findIndex(
                (x) => x.id === parseInt(v)
              )
              mergedWithConvData[index].nocAvail =
                (resData3.data[v] && resData3.data[v].settlement) || false
            })
          }
        }

        this.transactions[this.currentTransactionFilter] = mergedWithConvData
        return mergedWithConvData
      } catch (e) {
        return []
      }
    }
  },
  watch: {
    currentTransactionFilter() {
      if (this.currentTransactionFilter === 'advances') {
        this.changeTab('advances')
      } else if (this.currentTransactionFilter === 'payments') {
        this.changeTab('payments')
      } else {
        this.changeTab('settlements')
      }
    }
  },
  components: {
    Loader,
    DripChip,
    DownloadDropdown
  },
  mixins: [freshdeskMixin]
}
</script>
<style lang="scss">
.transactions-table {
  .transactions-card {
    overflow-x: scroll;
  }
}
#filters-dropdown {
  button {
    text-transform: capitalize;
    width: 100%;
  }
}
.transactions-invoices-filters {
  label {
    background: white;
  }
}
</style>
